import { Add } from '@mui/icons-material';
import ErrorIcon from '@mui/icons-material/Error';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Divider,
    Paper,
    Stack,
    Typography,
    lighten,
    useMediaQuery,
    type SxProps,
    type Theme,
} from '@mui/material';
import moment from 'moment';
import React, {
    ChangeEvent,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { Job, ProviderType } from '../../../../app/Slices/SevenDayApiTypes';
import CustomInputLabel from '../../../../layout/styledcomponents/CustomInputLabel';
import { CustomTextField } from '../../../../layout/styledcomponents/CustomTextField';
import { theme } from '../../../../shared/theme';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import { ServiceType, Status } from '../../../enums/contractor';
import { CleaningFeeType, ExperienceType } from '../../../enums/experience';
import { BookingSetMenuStatus } from '../../../enums/set-menu';
import { GuestRole } from '../../../enums/users';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import FormCheckbox from '../../../shared/FormCheckbox';
import FormCounter from '../../../shared/FormCounter';
import FormSelect from '../../../shared/FormSelect';
import FormTextField from '../../../shared/FormTextField';
import { ModuleContractor } from '../../../types/contractor';
import { BookingSetMenu, SetMenu } from '../../../types/set-menu';
import { User } from '../../../types/user';
import { usesNirovision } from '../../Experiences/utils';
import ExternalAttendees from '../ReservationRequest/FormSections/ReservationRequestSections/ExternalAttendees';
import { OwnerAttendeesSection } from '../ReservationRequest/FormSections/ReservationRequestSections/OwnerAttendeesSection';
import StaffAttendees from '../ReservationRequest/FormSections/ReservationRequestSections/StaffAttendees';
import ContractorPolicies from '../ReservationRequest/ReservationJobs/ContractorPolicies';
import ReservationJobs from '../ReservationRequest/ReservationJobs/ReservationJobs';
import {
    getSpacesWithAvailability,
    shouldShowSpaces,
} from '../ReservationRequest/ReservationRequestContent';
import Drivers from '../ReservationRequest/Shared/Drivers/Drivers';
import { getAvailableSpaces } from '../ReservationRequest/Shared/reservationUtils';
import ReservationSpaces from '../Spaces/ReservationSpaces';
import { LinkSx as ReservationDetailsLinkSx } from '../ViewReservations/SharedReseravtionComponents/ReservationDetailsSection';
import {
    maxSeatingFromCommercialSpaces,
    positiveGuestCap,
} from './reservationEdit.utils';
import {
    reservationEditFieldLabelSx,
    reservationEditFieldValueSx,
} from './reservationEditFieldStyles';

export type ReservationEditDetailsLayout = {
    cardSx: SxProps<Theme>;
    labelSx: SxProps<Theme>;
    valueSx: SxProps<Theme>;
    gridSx: SxProps<Theme>;
    rowSx: SxProps<Theme>;
};

const ReservationEditDetailsLayoutContext =
    React.createContext<ReservationEditDetailsLayout | null>(null);

function useReservationEditDetailsLayout(): ReservationEditDetailsLayout {
    const ctx = React.useContext(ReservationEditDetailsLayoutContext);
    if (!ctx) {
        throw new Error(
            'useReservationEditDetailsLayout must be used within ReservationEditDetailsSection',
        );
    }
    return ctx;
}

function useReservationEditDetailsLayoutStyles(
    isMobile: boolean,
): ReservationEditDetailsLayout {
    return useMemo(
        (): ReservationEditDetailsLayout => ({
            cardSx: {
                borderRadius: 2,
                py: isMobile ? 1 : 1.5,
                mb: isMobile ? 1 : 2,
            },
            labelSx: {
                ...reservationEditFieldLabelSx,
            },
            valueSx: {
                ...reservationEditFieldValueSx,
                wordBreak: 'break-word' as const,
            },
            gridSx: {
                display: 'grid',
                gridTemplateColumns: isMobile
                    ? '1fr'
                    : 'minmax(150px, 210px) 1fr',
                columnGap: isMobile ? 0 : 3,
                rowGap: isMobile ? 0.5 : 1.25,
                width: '100%',
                alignItems: 'center',
            },
            rowSx: { display: 'contents' as const },
        }),
        [isMobile],
    );
}

function DetailCard({
    children,
    sx,
}: {
    children: React.ReactNode;
    sx?: SxProps<Theme>;
}) {
    const { cardSx, gridSx } = useReservationEditDetailsLayout();
    return (
        <Box sx={sx ? ([cardSx, sx] as SxProps<Theme>) : cardSx}>
            <Box sx={gridSx}>{children}</Box>
        </Box>
    );
}

function DetailSurround({ children }: { children: React.ReactNode }) {
    const { cardSx } = useReservationEditDetailsLayout();
    return <Box sx={cardSx}>{children}</Box>;
}

type LabelRowProps = {
    label: React.ReactNode;
    children: React.ReactNode;
    required?: boolean;
    htmlFor?: string;
    id?: string;
    /** Sub-label weight (matches CustomInputLabel `fontWeight='none'`). */
    fontWeight?: React.ComponentProps<typeof CustomInputLabel>['fontWeight'];
};

type EditNavKey = 'details' | 'services' | 'notes' | 'issues';

function LabelRow({
    label,
    children,
    required,
    htmlFor,
    id,
    fontWeight,
}: LabelRowProps) {
    const { rowSx, valueSx } = useReservationEditDetailsLayout();
    return (
        <Box sx={rowSx}>
            <CustomInputLabel
                htmlFor={htmlFor}
                id={id}
                required={required}
                fontWeight={fontWeight}
                sx={{ mb: 0 }}
            >
                {label}
            </CustomInputLabel>

            <Box sx={{ minWidth: 0, width: '100%' }}>
                {typeof children === 'string' ||
                typeof children === 'number' ? (
                    <Typography variant='body3' sx={valueSx}>
                        {children}
                    </Typography>
                ) : (
                    children
                )}
            </Box>
        </Box>
    );
}

/** Same max width as date / time fields in this form */
const editDetailsControlMaxWidthPx = 280;

const editableControlWrapSx = {
    width: '100%',
    maxWidth: editDetailsControlMaxWidthPx,
    borderRadius: 1,
    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
    backgroundColor: '#fff',
    px: 0.25,
    py: 0.25,
    '& .MuiInputBase-root': {
        borderRadius: 1,
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'transparent',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'transparent',
        borderWidth: 1,
    },
    '&:focus-within': {
        borderColor: theme.palette.primary.main,
    },
};

const optionalServiceNotesSx = {
    width: '100%',
    borderRadius: 1,
    // border: `1px solid ${theme.palette.custom.lightBorderColor}`,
    backgroundColor: '#fff',
    // p: 0.5,
    '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        backgroundColor: '#fff',
    },
};

const charCountSx = {
    fontSize: 10,
    fontWeight: 600,
    color: 'text.secondary',
    mt: 0.5,
    letterSpacing: '0.02em',
};

function FullWidthRow({
    label,
    children,
    required,
    htmlFor,
    id,
    alignLabelToContentStart = false,
}: {
    label: React.ReactNode;
    children: React.ReactNode;
    required?: boolean;
    htmlFor?: string;
    id?: string;
    /** Top-align label with first line of value (e.g. multi-line attendee lists). */
    alignLabelToContentStart?: boolean;
}) {
    const { rowSx, labelSx } = useReservationEditDetailsLayout();
    const startAlignSx = alignLabelToContentStart
        ? { alignSelf: 'flex-start' as const }
        : {};
    return (
        <Box sx={rowSx}>
            {htmlFor ? (
                <CustomInputLabel
                    htmlFor={htmlFor}
                    id={id}
                    required={required}
                    sx={{ mb: 0, ...labelSx, ...startAlignSx }}
                >
                    {label}
                </CustomInputLabel>
            ) : (
                <Typography
                    variant='body3'
                    sx={{ ...labelSx, ...startAlignSx }}
                >
                    {label}
                    {required ? (
                        <Box
                            component='span'
                            sx={{
                                color: 'error.main',
                                ml: 0.4,
                                fontWeight: 900,
                            }}
                        >
                            *
                        </Box>
                    ) : null}
                </Typography>
            )}

            <Box
                sx={{
                    minWidth: 0,
                    width: '100%',
                    ...startAlignSx,
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

function CheckboxRowContent({ children }: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                alignSelf: 'flex-start',
                width: 'auto',
                maxWidth: '100%',
                '& .MuiFormControlLabel-root': {
                    m: 0,
                    mr: 0,
                    width: 'auto',
                    maxWidth: '100%',
                },
                '& .MuiCheckbox-root': {
                    p: 0,
                },
            }}
        >
            {children}
        </Box>
    );
}

function EditAccordionSection({
    title,
    subtitle,
    expanded,
    children,
}: {
    title: string;
    subtitle?: string;
    expanded: boolean;
    children: React.ReactNode;
}) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    return (
        <Accordion
            expanded={expanded}
            disableGutters
            square={false}
            sx={{
                mt: isMobile ? 1 : 2,
                borderRadius: 2,
                boxShadow: 'none',
                '&::before': { display: 'none' },
                overflow: 'hidden',
            }}
        >
            <AccordionSummary
                sx={{
                    py: 1.25,
                    px: 0,
                    backgroundColor: theme.palette.neutral.main,
                }}
            >
                <Box>
                    <Typography
                        component='h2'
                        variant='body1'
                        sx={{ fontWeight: 700, letterSpacing: 0.2 }}
                    >
                        {title}
                    </Typography>
                    {subtitle ? (
                        <Typography
                            variant='body3'
                            sx={{
                                mt: 0.35,
                                color: 'text.secondary',
                                fontSize: 13,
                                lineHeight: 1.35,
                                maxWidth: 640,
                            }}
                        >
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
            </AccordionSummary>

            <AccordionDetails
                role='region'
                aria-label={title}
                sx={{
                    px: 0,
                    pb: isMobile ? 1.25 : 3,
                    pt: 0,
                    backgroundColor: theme.palette.neutral.main,
                }}
            >
                <Divider sx={{ mt: 0, mb: isMobile ? 1 : 2 }} />
                {children}
            </AccordionDetails>
        </Accordion>
    );
}

function DetailsSubsection({
    title,
    description,
    children,
}: {
    title?: string;
    description?: string;
    children: React.ReactNode;
}) {
    const { labelSx } = useReservationEditDetailsLayout();
    return (
        <Box component='section' sx={{ scrollMarginTop: 72 }}>
            {title ? (
                <Typography
                    component='h3'
                    variant='body3'
                    sx={{
                        ...labelSx,
                        display: 'block',
                        mb: description ? 0.35 : 1,
                    }}
                >
                    {title}
                </Typography>
            ) : null}
            {description ? (
                <Typography
                    variant='body3'
                    sx={{
                        color: 'text.secondary',
                        fontSize: 13,
                        lineHeight: 1.35,
                        mb: 1,
                        maxWidth: 640,
                    }}
                >
                    {description}
                </Typography>
            ) : null}
            {children}
        </Box>
    );
}

function buildReservationDateOptions(startDate: any, endDate: any) {
    const dateArray: string[] = [];
    if (!startDate || !endDate) return dateArray;

    const start = moment(startDate, 'YYYY-MM-DD');
    const end = moment(endDate, 'YYYY-MM-DD');

    for (let d = start.clone(); d.isSameOrBefore(end, 'day'); d.add(1, 'day')) {
        dateArray.push(d.format('YYYY-MM-DD'));
    }

    return dateArray;
}

type Props = {
    activeNav: EditNavKey;
    reservationDetails: any;
    loggedInUser: any;
    getIndividualAsset: any;
    jobs?: Job[];
    guestUsers?: User[];
    isFetchingGuestUsers: boolean;
    availability: any;
    conflictError: string;
    errorMsg: any;
    compareCheckinSettings: any[];
    compareCheckoutSettings: any[];
    isOnBehalf: boolean;
    spaceError: boolean;
    setSpaceError: React.Dispatch<React.SetStateAction<boolean>>;
    onClickOpenDateModal: () => void;
    dateDisplay: string;
    canEditDate: boolean;
    /** Approved + currently within reservation dates: only attendee lists editable */
    attendeesOnlyEditMode: boolean;
    /** Merged business-rule + validation messages from EditReservationForm */
    reviewIssues: string[];
    setShouldCancelCatering: React.Dispatch<React.SetStateAction<boolean>>;
    shouldCancelCatering: boolean;
    incompleteJobs: (Job | BookingSetMenu)[];
};

export default function ReservationEditDetailsSection({
    activeNav,
    reservationDetails,
    loggedInUser,
    getIndividualAsset,
    jobs,
    guestUsers,
    isFetchingGuestUsers,
    availability,
    conflictError,
    compareCheckinSettings,
    compareCheckoutSettings,
    isOnBehalf,
    setSpaceError,
    onClickOpenDateModal,
    dateDisplay,
    canEditDate,
    attendeesOnlyEditMode,
    reviewIssues,
    setShouldCancelCatering,
    shouldCancelCatering,
    incompleteJobs,
}: Props) {
    const {
        control,
        watch,
        setValue,
        getValues,
        formState: { errors },
        trigger,
    } = useFormContext<any>();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const layoutStyles = useReservationEditDetailsLayoutStyles(isMobile);
    const { valueSx } = layoutStyles;

    if (Object.keys(errors).length) {
        console.error(errors);
    }

    const startDate = useWatch({ name: 'startDate', control });
    const endDate = useWatch({ name: 'endDate', control });
    const reservationReason = useWatch({ name: 'reason', control });
    const spaceType = useWatch({ name: 'bookingInfo.spaceType', control });
    const isMeetingRoom = watch('bookingInfo.reqMeetingRoom');
    const adultCount = useWatch({ name: 'bookingInfo.adults', control }) ?? 0;
    const childCount = useWatch({ name: 'bookingInfo.children', control }) ?? 0;
    const guestCount = useWatch({ name: 'bookingInfo.guests', control }) ?? 0;
    const overnightGuestCount =
        useWatch({ name: 'bookingInfo.overnightGuests', control }) ?? 0;

    const checkinTime =
        useWatch({ name: 'bookingInfo.checkinTime', control }) ?? '';
    const checkoutTime =
        useWatch({ name: 'bookingInfo.checkoutTime', control }) ?? '';

    const visitType =
        useWatch({ name: 'bookingInfo.visitType', control }) ?? '';

    const requiresCatering =
        useWatch({ name: 'bookingInfo.requiresCatering', control }) ?? false;
    const requiresBeverages =
        useWatch({ name: 'bookingInfo.requiresBeverages', control }) ?? false;
    const requiresMassages =
        useWatch({ name: 'bookingInfo.requiresMassages', control }) ?? false;

    const cateringDetails =
        useWatch({ name: 'bookingInfo.cateringNotes', control }) ?? '';
    const beverageDetails =
        useWatch({ name: 'bookingInfo.beverageNotes', control }) ?? '';
    const massageDetails =
        useWatch({ name: 'bookingInfo.massageNotes', control }) ?? '';

    const requiresServices = useWatch({ name: 'requiresServices', control });

    const additionalNotes =
        useWatch({ name: 'bookingInfo.notes', control }) ?? '';

    const spaces = useWatch({ name: 'spaces', control }) ?? [];

    const reservationDateOptions = useMemo(
        () => buildReservationDateOptions(startDate, endDate),
        [startDate, endDate],
    );

    const showOptionalServicesSection =
        !!getIndividualAsset?.details?.catering ||
        !!getIndividualAsset?.details?.alcohol ||
        !!getIndividualAsset?.details?.massages;
    const bookerType = reservationDetails?.bookerType;

    const readableStartDate = moment(startDate).format('DD/MM/YYYY');
    const readableEndDate = moment(endDate).format('DD/MM/YYYY');

    const maxGuestCountReached =
        adultCount + childCount >=
        (getIndividualAsset?.details?.maxGuests ?? Infinity);

    const maxOvernightGuestReached =
        overnightGuestCount >=
        (getIndividualAsset?.details?.overnightGuests ?? Infinity);

    const rawOnBehalf = reservationDetails?.onBehalf ?? isOnBehalf;
    const isReservationOnBehalf =
        rawOnBehalf === true || rawOnBehalf === 'true' || rawOnBehalf === 1;

    /** Nirovision: lock guest count for owner editing a guest's booking — not for on-behalf (owner-made for a guest). */
    const disableGuests =
        loggedInUser?.role.includes('owner') &&
        bookerType === 'guest' &&
        getIndividualAsset?.usesNirovision &&
        !isReservationOnBehalf;

    const availableSpaces = getAvailableSpaces(
        availability,
        getIndividualAsset,
    );

    const isDisabled = (value: string) => {
        const found = availableSpaces.find((s: any) => s.value === value);
        return !!found?.isUnavailable;
    };

    const hasByZoneCleaningFee = getIndividualAsset?.charges?.find(
        (charge: any) =>
            charge.chargeType === 'cleaningFee' &&
            charge.cleaningFeeType === CleaningFeeType.ByZone,
    );

    const showSpaceSelection = useMemo(
        () =>
            shouldShowSpaces({
                experienceDetails: getIndividualAsset,
                hasByZoneCleaningFee,
                spaceType,
                isMeetingRoom,
                isOwner: loggedInUser?.role.includes('owner'),
                isOnBehalf,
            }),
        [
            getIndividualAsset,
            hasByZoneCleaningFee,
            spaceType,
            isMeetingRoom,
            loggedInUser?.role,
            isOnBehalf,
        ],
    );

    useEffect(() => {
        if (!showSpaceSelection) {
            setSpaceError(false);
        }
    }, [showSpaceSelection]);

    const spacesWithAvailability = useMemo(
        () =>
            getSpacesWithAvailability({
                experienceDetails: getIndividualAsset,
                hasByZoneCleaningFee,
                spaceType,
            }),
        [getIndividualAsset, hasByZoneCleaningFee, spaceType],
    );

    const commercialAttendees =
        getIndividualAsset?.type === ExperienceType.Commercial &&
        (spaceType === 'meetingRoom' ||
            spaceType === 'entireVenue' ||
            isMeetingRoom);

    const mustCompleteMeetingRoomDetails =
        getIndividualAsset?.type === ExperienceType.Commercial &&
        isMeetingRoom &&
        spaceType !== 'meetingRoom' &&
        spaceType !== 'entireVenue';

    const boardroomSeatingCap = useMemo(
        () => maxSeatingFromCommercialSpaces(spaces),
        [spaces],
    );

    /**
     * Module-level caps can be wrong or missing while the selected boardroom has
     * `seatingCapacity` (e.g. 4). Use the higher of module vs selected spaces, and
     * treat 0 / invalid module caps as “no module limit” when spaces provide a cap.
     */
    const effectiveCommercialGuestMax = useMemo(() => {
        if (getIndividualAsset?.type !== ExperienceType.Commercial) {
            return Number.POSITIVE_INFINITY;
        }
        const details = getIndividualAsset?.details;
        if (spaceType === 'meetingRoom' || isMeetingRoom) {
            const moduleCap = positiveGuestCap(details?.meetingRoomMaxGuests);
            const combined = Math.max(moduleCap ?? 0, boardroomSeatingCap);
            return combined > 0 ? combined : Number.POSITIVE_INFINITY;
        }
        if (spaceType === 'entireVenue') {
            const cap = positiveGuestCap(details?.maxGuests);
            return cap ?? Number.POSITIVE_INFINITY;
        }
        return 1;
    }, [
        getIndividualAsset?.type,
        getIndividualAsset?.details?.maxGuests,
        getIndividualAsset?.details?.meetingRoomMaxGuests,
        spaceType,
        isMeetingRoom,
        boardroomSeatingCap,
    ]);

    const MAX_COMMERCIAL_GUEST_REACHED =
        getIndividualAsset?.type === ExperienceType.Commercial &&
        Number(guestCount ?? 0) >= effectiveCommercialGuestMax;

    const commercialHasExistingBooking =
        availability?.desks < getIndividualAsset?.details?.desks ||
        availability?.privateOffices <
            getIndividualAsset?.details?.privateOffices ||
        availability?.meetingRooms < getIndividualAsset?.details?.meetingRooms;

    const hasJobs =
        (jobs?.filter(
            (job: Job) =>
                job.serviceType !== ServiceType.Cleaning &&
                ![Status.Cancelled, Status.Declined].includes(job.status),
        )?.length ?? 0) > 0 ||
        reservationDetails?.setMenus.filter((setMenu: BookingSetMenu) =>
            [
                BookingSetMenuStatus.Active,
                BookingSetMenuStatus.PendingBooking,
            ].includes(setMenu.status),
        ).length > 0;

    const experienceContractors: ModuleContractor[] =
        getIndividualAsset?.contractors ?? [];
    const experienceSetMenus: SetMenu[] = getIndividualAsset?.setMenus ?? [];

    const shouldShowJobForm =
        experienceContractors.length + experienceSetMenus.length > 0;

    const usesFaceRec = usesNirovision(getIndividualAsset);

    const jobsRoot = useWatch({ name: 'jobs' as any, control });

    const jobsArray: (Job | BookingSetMenu)[] = Object.values(jobsRoot ?? {});

    const contractorJobsArray = jobsArray.filter(
        (job) => job.providerType === ProviderType.Contractor,
    ) as Job[];

    const uniqueContractorIds = new Set(
        contractorJobsArray.map((contractorJob) => contractorJob.contractor.id),
    );

    const contractorsForPolicies = experienceContractors.filter(
        (c) => c.cancellationPolicy && uniqueContractorIds.has(c.id),
    );

    const policyIdsKey = useMemo(
        () => contractorsForPolicies.map((c: any) => c.id).join('|'),
        [contractorsForPolicies],
    );

    const policiesSyncKey = useMemo(
        () =>
            jobsArray
                .filter(
                    (j: any) =>
                        j?.contractor?.id &&
                        ![Status.Cancelled, Status.Declined].includes(
                            j?.status,
                        ),
                )
                .map((j: any) => `${j.contractor?.id}:${j.booking ? '1' : '0'}`)
                .sort()
                .join('|'),
        [jobsArray],
    );

    const prevContractorPolicyIdsRef = useRef<number[]>([]);

    useEffect(() => {
        if (!getIndividualAsset) return;

        if (contractorsForPolicies.length === 0) {
            prevContractorPolicyIdsRef.current = [];
            setValue('contractorPolicies', [], {
                shouldDirty: false,
                shouldValidate: false,
            });
            return;
        }

        const prevIds = prevContractorPolicyIdsRef.current;
        const prevRows = getValues('contractorPolicies') ?? [];

        const next = contractorsForPolicies.map((c: any) => {
            const hasPersistedJob = jobsArray.some(
                (j: any) =>
                    j.booking &&
                    j.contractor?.id === c.id &&
                    ![Status.Cancelled, Status.Declined].includes(j.status),
            );
            if (hasPersistedJob) return { checked: true };

            const prevIdx = prevIds.indexOf(c.id);
            if (prevIdx >= 0 && prevRows[prevIdx])
                return { checked: !!prevRows[prevIdx].checked };
            return { checked: false };
        });

        const currentRows = getValues('contractorPolicies') ?? [];
        const unchanged =
            currentRows.length === next.length &&
            next.every((row, i) => !!row.checked === !!currentRows[i]?.checked);
        if (unchanged) {
            prevContractorPolicyIdsRef.current = contractorsForPolicies.map(
                (c: any) => c.id,
            );
            return;
        }

        setValue('contractorPolicies', next, {
            shouldDirty: false,
            shouldValidate: false,
        });
        prevContractorPolicyIdsRef.current = contractorsForPolicies.map(
            (c: any) => c.id,
        );
    }, [
        policyIdsKey,
        policiesSyncKey,
        getIndividualAsset,
        setValue,
        getValues,
    ]);

    useEffect(() => {
        if (!spaceType) return;

        const currentSpaces = watch('spaces') ?? [];
        let nextSpaces = currentSpaces;

        if (spaceType === 'desk' && !isMeetingRoom) {
            nextSpaces = [];
        } else {
            if (spaceType !== 'meetingRoom' && !isMeetingRoom) {
                nextSpaces = nextSpaces.filter(
                    (space: any) =>
                        space?.type !== 'boardroom' &&
                        space?.spaceType !== 'boardroom',
                );
            }

            if (spaceType !== 'privateOffice') {
                nextSpaces = nextSpaces.filter(
                    (space: any) =>
                        space?.type !== 'privateOffice' &&
                        space?.spaceType !== 'privateOffice',
                );
            }
        }

        if (JSON.stringify(currentSpaces) !== JSON.stringify(nextSpaces)) {
            setValue('spaces', nextSpaces);
        }
    }, [spaceType, isMeetingRoom, setValue, watch]);

    useEffect(() => {
        const value = watch('bookingInfo.reqMeetingRoom');

        if (value === 'true') {
            setValue('bookingInfo.reqMeetingRoom', true, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }

        if (value === 'false') {
            setValue('bookingInfo.reqMeetingRoom', false, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    }, [watch('bookingInfo.reqMeetingRoom'), setValue]);

    const showReadOnlyApproved =
        reservationDetails?.status === 'approved' &&
        !loggedInUser?.role.includes('owner');

    const lockNonAttendeeFields = attendeesOnlyEditMode;
    const detailFieldsReadOnly = showReadOnlyApproved || lockNonAttendeeFields;
    // Approved reservations should still allow optional services edits.
    const optionalServicesReadOnly = false;

    const nonInteractiveLockSx = lockNonAttendeeFields
        ? {
              pointerEvents: 'none' as const,
              opacity: 0.72,
              userSelect: 'none' as const,
          }
        : undefined;

    const ownerBlockingOwnAsset =
        loggedInUser?.role.includes('owner') &&
        reservationDetails?.onBehalf === false;

    const showNotesSection =
        !ownerBlockingOwnAsset || reservationReason === 'other';

    const canEditMeetingRoomRequired =
        spaceType === 'desk' || spaceType === 'privateOffice';

    const ownerCommercialAttendeesReadOnly =
        loggedInUser?.role.includes('owner') &&
        getIndividualAsset?.type === ExperienceType.Commercial;

    const renderAttendeeList = (
        users: User[] | undefined,
        emptyText: string,
    ) => {
        if (!users || users.length === 0) {
            return <Typography variant='body3'>{emptyText}</Typography>;
        }

        return (
            <Box component='ul' sx={{ pl: 2, m: 0 }}>
                {users.map((user) => (
                    <Typography key={user.id} component='li' variant='body3'>
                        {`${user.firstName} ${user.lastName} - ${user.email}`}
                    </Typography>
                ))}
            </Box>
        );
    };

    const hasIssuesSection = reviewIssues.length > 0;

    const handleSpaceTypeChange = (type: string) => {
        if (requiresServices) {
            if (
                !['entireVenue', 'meetingRoom'].includes(type) &&
                !isMeetingRoom
            ) {
                setShouldCancelCatering(true);
            } else {
                setShouldCancelCatering(false);
            }
        }
    };
    const handlReqMeetingRoomChange = (checked: boolean) => {
        if (requiresServices) {
            if (
                !['entireVenue', 'meetingRoom'].includes(spaceType) &&
                !checked
            ) {
                setShouldCancelCatering(true);
            } else {
                setShouldCancelCatering(false);
            }
        }
    };
    const handleRequiresServiceChange = (
        serviceType: ServiceType | undefined,
        checked: boolean,
    ) => {
        if (!checked) {
            trigger('bookingInfo');
            const incompleteServiceJobs = incompleteJobs.filter((job) =>
                serviceType === undefined
                    ? true
                    : job.serviceType === serviceType,
            );
            if (incompleteServiceJobs.length === 0) return;

            const jobsRecord = getValues('jobs') ?? {};
            const next = { ...jobsRecord };

            for (const job of incompleteServiceJobs) {
                delete next[String(job.id)];
            }

            const serviceTypeKeys = new Set(Object.values(ServiceType));
            if (serviceType === undefined) {
                for (const key of Object.keys(next)) {
                    if (serviceTypeKeys.has(key as ServiceType)) {
                        delete next[key];
                    }
                }
            } else {
                delete next[serviceType];
            }

            setValue('jobs', next, { shouldDirty: true });
        }
    };

    const ExperienceLink = ({
        type,
        id,
        name,
    }: {
        type: string;
        id: string | number;
        name: string;
    }) => (
        <Box
            component={RouterLink}
            to={`/viewExperiences/${type}/${id}`}
            sx={ReservationDetailsLinkSx}
        >
            {name}
        </Box>
    );
    const shortAddress = `${getIndividualAsset?.suburb} ${getIndividualAsset?.postcode} ${getIndividualAsset?.state} ${getIndividualAsset?.countryCode}`;
    const ValueLink = ({
        to,
        children,
    }: {
        to: string;
        children: React.ReactNode;
    }) => (
        <Box
            component='a'
            href={to}
            target='_blank'
            rel='noreferrer'
            sx={ReservationDetailsLinkSx}
        >
            {children}
        </Box>
    );

    return (
        <ReservationEditDetailsLayoutContext.Provider value={layoutStyles}>
            <Box>
                {activeNav === 'details' && (
                    <EditAccordionSection
                        title='Reservation Details'
                        expanded={true}
                    >
                        <>
                            <DetailCard>
                                <LabelRow label='Experience'>
                                    <Typography variant='body3' sx={valueSx}>
                                        {reservationDetails?.module
                                            ?.isAvailable ? (
                                            <ExperienceLink
                                                type={
                                                    reservationDetails?.module
                                                        ?.type
                                                }
                                                id={
                                                    reservationDetails?.module
                                                        ?.id
                                                }
                                                name={
                                                    reservationDetails?.module
                                                        ?.name
                                                }
                                            />
                                        ) : (
                                            <span>
                                                {
                                                    reservationDetails?.module
                                                        ?.name
                                                }
                                            </span>
                                        )}
                                        {reservationDetails?.owner?.name ? (
                                            <>
                                                {' '}
                                                - hosted by{' '}
                                                {reservationDetails.owner.name}
                                            </>
                                        ) : null}
                                    </Typography>
                                </LabelRow>

                                <LabelRow label='Type'>
                                    <Typography variant='body3' sx={valueSx}>
                                        {camelCaseToTitleCase(
                                            reservationDetails?.module?.type,
                                        )}
                                    </Typography>
                                </LabelRow>
                            </DetailCard>

                            <DetailCard>
                                <LabelRow label='Location'>
                                    <Typography variant='body3' sx={valueSx}>
                                        {reservationDetails?.module
                                            ?.isAvailable ? (
                                            <ValueLink
                                                to={`https://www.google.com/maps/place/${encodeURIComponent(
                                                    shortAddress,
                                                )}`}
                                            >
                                                {shortAddress}
                                            </ValueLink>
                                        ) : (
                                            'This experience is no longer available'
                                        )}
                                    </Typography>
                                </LabelRow>
                            </DetailCard>

                            <DetailCard>
                                <LabelRow label='Date'>
                                    {canEditDate ? (
                                        <Box
                                            sx={{
                                                ...editableControlWrapSx,
                                                '& .MuiInputBase-input': {
                                                    ...theme.typography.body3,
                                                },
                                                '& .MuiAutocomplete-input': {
                                                    ...theme.typography.body3,
                                                },
                                            }}
                                        >
                                            <CustomTextField
                                                fullWidth
                                                value={dateDisplay}
                                                onClick={onClickOpenDateModal}
                                                color='dark'
                                                inputProps={{
                                                    'aria-label': 'Date',
                                                    style: {
                                                        backgroundColor:
                                                            'transparent',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant='body3'
                                            sx={valueSx}
                                        >
                                            {dateDisplay}
                                        </Typography>
                                    )}
                                </LabelRow>

                                <LabelRow
                                    label={
                                        getIndividualAsset?.type ===
                                        'commercial'
                                            ? 'Access From'
                                            : 'Checkin'
                                    }
                                    htmlFor='checkin-time__input'
                                    id='checkin-time__label'
                                >
                                    {ownerBlockingOwnAsset ? (
                                        '-'
                                    ) : detailFieldsReadOnly ? (
                                        <Typography
                                            variant='body3'
                                            sx={valueSx}
                                        >
                                            {checkinTime}
                                        </Typography>
                                    ) : (
                                        <Box
                                            sx={{
                                                ...editableControlWrapSx,
                                                '& .MuiInputBase-input': {
                                                    ...theme.typography.body3,
                                                },
                                                '& .MuiAutocomplete-input': {
                                                    ...theme.typography.body3,
                                                },
                                            }}
                                        >
                                            <FormAutocomplete
                                                id='checkin-time__input'
                                                name='bookingInfo.checkinTime'
                                                control={control}
                                                options={compareCheckinSettings}
                                                onOptionSelect={() => {
                                                    setValue('spaces', []);
                                                }}
                                            />
                                        </Box>
                                    )}
                                </LabelRow>

                                <LabelRow
                                    label={
                                        getIndividualAsset?.type ===
                                        'commercial'
                                            ? 'Access To'
                                            : 'Checkout'
                                    }
                                    htmlFor='checkout-time__input'
                                    id='checkout-time__label'
                                >
                                    {ownerBlockingOwnAsset ? (
                                        '-'
                                    ) : detailFieldsReadOnly ? (
                                        <Typography
                                            variant='body3'
                                            sx={valueSx}
                                        >
                                            {checkoutTime}
                                        </Typography>
                                    ) : (
                                        <Box
                                            sx={{
                                                ...editableControlWrapSx,
                                                '& .MuiInputBase-input': {
                                                    ...theme.typography.body3,
                                                },
                                                '& .MuiAutocomplete-input': {
                                                    ...theme.typography.body3,
                                                },
                                            }}
                                        >
                                            <FormAutocomplete
                                                id='checkout-time__input'
                                                name='bookingInfo.checkoutTime'
                                                control={control}
                                                disabled={detailFieldsReadOnly}
                                                options={
                                                    compareCheckoutSettings
                                                }
                                                onOptionSelect={() => {
                                                    setValue('spaces', []);
                                                }}
                                            />
                                        </Box>
                                    )}
                                </LabelRow>

                                {getIndividualAsset?.type === 'commercial' && (
                                    <LabelRow label='Availability'>
                                        <Typography
                                            variant='body3'
                                            sx={valueSx}
                                        >
                                            {availability?.desks > 0 && (
                                                <>
                                                    Dedicated Desks:{' '}
                                                    <strong>
                                                        {availability?.desks}
                                                    </strong>{' '}
                                                    -{' '}
                                                </>
                                            )}
                                            {availability?.privateOffices >
                                                0 && (
                                                <>
                                                    {getIndividualAsset?.details
                                                        ?.subType === 'house'
                                                        ? 'Rooms '
                                                        : 'Private Offices '}
                                                    <strong>
                                                        {
                                                            availability?.privateOffices
                                                        }
                                                    </strong>{' '}
                                                    -{' '}
                                                </>
                                            )}
                                            {availability?.meetingRooms > 0 && (
                                                <>
                                                    Meeting Rooms{' '}
                                                    <strong>
                                                        {
                                                            availability?.meetingRooms
                                                        }
                                                    </strong>
                                                </>
                                            )}
                                        </Typography>
                                    </LabelRow>
                                )}

                                <LabelRow label='Status'>
                                    <Typography variant='body3' sx={valueSx}>
                                        {camelCaseToTitleCase(
                                            reservationDetails?.status ?? '',
                                        )}
                                    </Typography>
                                </LabelRow>
                            </DetailCard>

                            {reservationDetails?.bookerType.includes('owner') &&
                            !reservationDetails?.onBehalf ? (
                                <DetailCard>
                                    <>
                                        <LabelRow label='Reserved By'>
                                            <Typography variant='body3'>{`${reservationDetails?.booker?.firstName} ${reservationDetails?.booker?.lastName} - ${reservationDetails?.booker?.email} (${reservationDetails?.owner?.name})`}</Typography>
                                        </LabelRow>

                                        <LabelRow
                                            label='Reservation Reason'
                                            htmlFor='reason__input'
                                            id='reason__label'
                                        >
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 260,
                                                    '& .MuiInputBase-input': {
                                                        ...theme.typography
                                                            .body3,
                                                    },
                                                }}
                                            >
                                                <FormSelect
                                                    labelId='reason__label'
                                                    id='reason__input'
                                                    name='reason'
                                                    control={control}
                                                    disabled={
                                                        lockNonAttendeeFields
                                                    }
                                                    options={[
                                                        {
                                                            value: 'maintenance',
                                                            label: 'Maintenance',
                                                        },
                                                        {
                                                            value: 'publicHoliday',
                                                            label: 'Public Holiday',
                                                        },
                                                        {
                                                            value: 'staffAway',
                                                            label: 'Staff Away',
                                                        },
                                                        {
                                                            value: 'shutdownPeriod',
                                                            label: 'Shutdown Period',
                                                        },
                                                        {
                                                            value: 'other',
                                                            label: 'Other',
                                                        },
                                                    ]}
                                                    MenuProps={{
                                                        PaperProps: {
                                                            sx: {
                                                                '& .MuiMenuItem-root':
                                                                    {
                                                                        ...theme
                                                                            .typography
                                                                            .body3,
                                                                    },
                                                            },
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </LabelRow>
                                        {showNotesSection && (
                                            <LabelRow
                                                label='Notes'
                                                htmlFor='notes__input'
                                                required={
                                                    loggedInUser?.role?.includes(
                                                        'owner',
                                                    ) &&
                                                    reservationReason ===
                                                        'other'
                                                }
                                            >
                                                <Stack
                                                    spacing={1}
                                                    sx={{ width: '100%' }}
                                                >
                                                    <FormTextField
                                                        id='notes__input'
                                                        name='notes'
                                                        control={control}
                                                        disabled={
                                                            lockNonAttendeeFields
                                                        }
                                                        placeholder={
                                                            reservationReason ===
                                                                'other' &&
                                                            loggedInUser?.role?.includes(
                                                                'owner',
                                                            )
                                                                ? "Enter in 'other' reason"
                                                                : ''
                                                        }
                                                        fullWidth
                                                        inputProps={{
                                                            maxLength: 500,
                                                        }}
                                                        multiline
                                                        rows={4}
                                                    />

                                                    {conflictError ? (
                                                        <Box
                                                            sx={{
                                                                backgroundColor:
                                                                    lighten(
                                                                        theme
                                                                            .palette
                                                                            .warning
                                                                            .main,
                                                                        0.8,
                                                                    ),
                                                                width: '100%',
                                                                borderRadius: 2,
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    color: 'red',
                                                                    p: 1,
                                                                    pl: 1.25,
                                                                    fontSize:
                                                                        '12px',
                                                                    display:
                                                                        'flex',
                                                                    gap: 1,
                                                                    alignItems:
                                                                        'center',
                                                                }}
                                                            >
                                                                <ErrorIcon />
                                                                <span
                                                                    style={{
                                                                        color: 'red',
                                                                        fontSize:
                                                                            '10px',
                                                                    }}
                                                                >
                                                                    {
                                                                        conflictError
                                                                    }
                                                                </span>
                                                            </Typography>
                                                        </Box>
                                                    ) : null}
                                                </Stack>
                                            </LabelRow>
                                        )}

                                        <LabelRow label='Approved'>
                                            <Typography
                                                variant='body3'
                                                sx={valueSx}
                                            >
                                                {reservationDetails?.approvedBy
                                                    ?.firstName
                                                    ? `${moment(
                                                          reservationDetails?.approvedAt,
                                                      ).format(
                                                          'DD/MM/YYYY',
                                                      )} - ${
                                                          reservationDetails
                                                              ?.approvedBy
                                                              ?.firstName
                                                      } ${
                                                          reservationDetails
                                                              ?.approvedBy
                                                              ?.lastName
                                                      } ${reservationDetails?.approvedBy?.email}`
                                                    : null}
                                            </Typography>
                                        </LabelRow>
                                        {getIndividualAsset?.type !==
                                            ExperienceType.Vehicle && (
                                            <FullWidthRow
                                                label='Guest Attendees'
                                                htmlFor='staff-attending__input'
                                                id='guest-attendees__label'
                                                alignLabelToContentStart
                                                required={
                                                    mustCompleteMeetingRoomDetails
                                                }
                                            >
                                                <OwnerAttendeesSection
                                                    experienceDetails={
                                                        getIndividualAsset
                                                    }
                                                    usesFaceRec={usesFaceRec}
                                                    isEditing={true}
                                                />
                                            </FullWidthRow>
                                        )}
                                    </>
                                </DetailCard>
                            ) : (
                                <>
                                    <DetailCard
                                        sx={{
                                            mb: { xs: 1, md: 1.25 },
                                        }}
                                    >
                                        <LabelRow label='Company'>
                                            <Typography
                                                variant='body3'
                                                sx={valueSx}
                                            >
                                                {reservationDetails?.guest?.name
                                                    ? reservationDetails?.guest
                                                          ?.name
                                                    : reservationDetails
                                                            ?.bookingInfo
                                                            ?.visitType
                                                      ? `Reserved on behalf of ${reservationDetails?.owner?.name}`
                                                      : `Blocked by ${reservationDetails?.owner?.name}`}
                                            </Typography>
                                        </LabelRow>

                                        <LabelRow
                                            label='Primary Guest'
                                            htmlFor='primary-guest__input'
                                            id='primary-guest__label'
                                        >
                                            {loggedInUser.role ===
                                                'guest_admin' ||
                                            loggedInUser.role ===
                                                'guest_department_admin' ? (
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 260,
                                                        '& .MuiInputBase-input':
                                                            {
                                                                ...theme
                                                                    .typography
                                                                    .body3,
                                                            },
                                                        '& .MuiAutocomplete-input':
                                                            {
                                                                ...theme
                                                                    .typography
                                                                    .body3,
                                                            },
                                                    }}
                                                >
                                                    <FormAutocomplete
                                                        id='primary-guest__input'
                                                        name='booker.id'
                                                        control={control}
                                                        options={
                                                            guestUsers
                                                                ?.filter(
                                                                    ({
                                                                        id,
                                                                        isActive,
                                                                    }: User) =>
                                                                        isActive ||
                                                                        id ===
                                                                            reservationDetails
                                                                                ?.booker
                                                                                ?.id,
                                                                )
                                                                .map(
                                                                    ({
                                                                        id,
                                                                        email,
                                                                        firstName,
                                                                        lastName,
                                                                    }: User) => ({
                                                                        value: id,
                                                                        label: `${firstName} ${lastName} (${email})`,
                                                                    }),
                                                                ) || []
                                                        }
                                                        PaperComponent={({
                                                            children,
                                                        }) => (
                                                            <Paper>
                                                                <Box
                                                                    sx={{
                                                                        px: 1,
                                                                        pt: 1,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        sx={{
                                                                            fontWeight: 800,
                                                                            fontSize: 12,
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .secondary,
                                                                            mb: 1,
                                                                        }}
                                                                    >
                                                                        Select
                                                                        primary
                                                                        guest
                                                                    </Typography>
                                                                </Box>

                                                                <Box
                                                                    sx={{
                                                                        px: 1,
                                                                        pb: 1,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        component='button'
                                                                        type='button'
                                                                        onMouseDown={() =>
                                                                            window.open(
                                                                                '/newUser',
                                                                                '_blank',
                                                                            )
                                                                        }
                                                                        style={{
                                                                            width: '100%',
                                                                            border: 'none',
                                                                            background:
                                                                                'transparent',
                                                                            padding: 0,
                                                                            cursor: 'pointer',
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                display:
                                                                                    'flex',
                                                                                alignItems:
                                                                                    'center',
                                                                                gap: 1,
                                                                                py: 1,
                                                                                px: 1.5,
                                                                                borderRadius: 2,
                                                                                border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                                                                backgroundColor:
                                                                                    theme
                                                                                        .palette
                                                                                        .neutral
                                                                                        .main,
                                                                            }}
                                                                        >
                                                                            <Add />
                                                                            <Typography
                                                                                sx={{
                                                                                    fontWeight: 800,
                                                                                    fontSize: 13,
                                                                                }}
                                                                            >
                                                                                Create
                                                                                New
                                                                                User
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>

                                                                <Typography
                                                                    variant='body3'
                                                                    sx={valueSx}
                                                                >
                                                                    {children}
                                                                </Typography>
                                                            </Paper>
                                                        )}
                                                        disabled={
                                                            lockNonAttendeeFields ||
                                                            (loggedInUser?.role !==
                                                                GuestRole.Admin &&
                                                                loggedInUser?.role !==
                                                                    GuestRole.DepartmentAdmin &&
                                                                !isFetchingGuestUsers)
                                                        }
                                                    />
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant='body3'
                                                    sx={valueSx}
                                                >
                                                    {' '}
                                                    {
                                                        reservationDetails
                                                            ?.booker?.firstName
                                                    }{' '}
                                                    {
                                                        reservationDetails
                                                            ?.booker?.lastName
                                                    }{' '}
                                                    -{' '}
                                                    {
                                                        reservationDetails
                                                            ?.booker?.email
                                                    }
                                                </Typography>
                                            )}
                                        </LabelRow>

                                        {reservationDetails?.createdBy &&
                                            reservationDetails?.createdBy
                                                ?.email !==
                                                reservationDetails?.booker
                                                    ?.email && (
                                                <LabelRow label='Reservation made by'>
                                                    <Typography
                                                        variant='body3'
                                                        sx={valueSx}
                                                    >
                                                        {' '}
                                                        {`${reservationDetails?.createdBy?.firstName} ${reservationDetails?.createdBy?.lastName} - ${reservationDetails?.createdBy?.email}`}
                                                    </Typography>
                                                </LabelRow>
                                            )}

                                        <LabelRow label='Type of visit'>
                                            <Typography
                                                variant='body3'
                                                sx={valueSx}
                                            >
                                                {visitType
                                                    ?.charAt(0)
                                                    ?.toUpperCase() +
                                                    visitType?.slice(1)}
                                            </Typography>
                                        </LabelRow>
                                    </DetailCard>

                                    {(getIndividualAsset?.type ===
                                        ExperienceType.Vehicle ||
                                        getIndividualAsset?.type ===
                                            ExperienceType.Commercial ||
                                        spaceType === 'desk' ||
                                        spaceType === 'privateOffice' ||
                                        showSpaceSelection) && (
                                        <DetailCard>
                                            {getIndividualAsset?.type ===
                                                ExperienceType.Vehicle && (
                                                <FullWidthRow label='Driver Information'>
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            maxWidth:
                                                                editDetailsControlMaxWidthPx,
                                                        }}
                                                    >
                                                        <Drivers />
                                                    </Box>
                                                </FullWidthRow>
                                            )}

                                            {getIndividualAsset?.type ===
                                                ExperienceType.Commercial && (
                                                <LabelRow
                                                    label='Workspace Type'
                                                    htmlFor='space-type__input'
                                                    id='space-type__label'
                                                >
                                                    {(reservationDetails?.status ===
                                                        'approved' &&
                                                        !loggedInUser?.role.includes(
                                                            'owner',
                                                        )) ||
                                                    disableGuests ||
                                                    lockNonAttendeeFields ? (
                                                        <Typography
                                                            variant='body3'
                                                            sx={valueSx}
                                                        >
                                                            {spaceType ===
                                                                'privateOffice' &&
                                                            getIndividualAsset
                                                                ?.details
                                                                ?.subType !==
                                                                'house'
                                                                ? 'Private Office'
                                                                : spaceType ===
                                                                        'privateOffice' &&
                                                                    getIndividualAsset
                                                                        ?.details
                                                                        ?.subType ===
                                                                        'house'
                                                                  ? 'Room'
                                                                  : spaceType ===
                                                                      'meetingRoom'
                                                                    ? 'Meeting Room'
                                                                    : spaceType ===
                                                                        'desk'
                                                                      ? 'Dedicated Desk'
                                                                      : spaceType ===
                                                                          'entireVenue'
                                                                        ? 'Entire Venue'
                                                                        : spaceType}
                                                        </Typography>
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                width: '100%',
                                                                maxWidth: 260,

                                                                '& .MuiInputBase-input':
                                                                    {
                                                                        ...theme
                                                                            .typography
                                                                            .body3,
                                                                    },
                                                                '& .MuiAutocomplete-input':
                                                                    {
                                                                        ...theme
                                                                            .typography
                                                                            .body3,
                                                                    },
                                                            }}
                                                        >
                                                            <FormAutocomplete
                                                                id='space-type__input'
                                                                name='bookingInfo.spaceType'
                                                                control={
                                                                    control
                                                                }
                                                                disabled={
                                                                    detailFieldsReadOnly
                                                                }
                                                                getOptionDisabled={(
                                                                    option: any,
                                                                ) =>
                                                                    (option.value ===
                                                                        'entireVenue' &&
                                                                        commercialHasExistingBooking) ||
                                                                    isDisabled(
                                                                        option.value,
                                                                    )
                                                                }
                                                                onOptionSelect={(
                                                                    option: any,
                                                                ) => {
                                                                    handleSpaceTypeChange(
                                                                        option.value,
                                                                    );
                                                                }}
                                                                options={[
                                                                    ...availableSpaces,
                                                                    {
                                                                        value: 'entireVenue',
                                                                        label: `Entire Venue ${
                                                                            commercialHasExistingBooking
                                                                                ? `(unavailable)`
                                                                                : ``
                                                                        }`,
                                                                    },
                                                                ]}
                                                            />
                                                            {shouldCancelCatering &&
                                                                hasJobs &&
                                                                ![
                                                                    'entireVenue',
                                                                    'meetingRoom',
                                                                ].includes(
                                                                    spaceType,
                                                                ) && (
                                                                    <Alert
                                                                        severity='warning'
                                                                        sx={{
                                                                            width: '100%',
                                                                            borderRadius: 2,
                                                                            alignSelf:
                                                                                'stretch',
                                                                            '& .MuiAlert-message':
                                                                                {
                                                                                    ...theme
                                                                                        .typography
                                                                                        .body3,
                                                                                },
                                                                        }}
                                                                    >
                                                                        Saving
                                                                        this
                                                                        change
                                                                        will
                                                                        cancel
                                                                        all
                                                                        catering
                                                                        jobs
                                                                        created
                                                                        for this
                                                                        reservation.
                                                                    </Alert>
                                                                )}
                                                        </Box>
                                                    )}

                                                    {availability?.desks < 1 &&
                                                        spaceType ===
                                                            'desk' && (
                                                            <Typography
                                                                variant='body3'
                                                                sx={{
                                                                    color: 'error.main',
                                                                    fontSize: 14,
                                                                    lineHeight: 1.45,
                                                                    wordBreak:
                                                                        'break-word' as const,
                                                                }}
                                                            >
                                                                There are not
                                                                enough desks
                                                                available.
                                                                Please change
                                                                the date.
                                                            </Typography>
                                                        )}
                                                    {availability?.meetingRooms <
                                                        1 &&
                                                        spaceType ===
                                                            'meetingRoom' && (
                                                            <Typography
                                                                variant='body3'
                                                                sx={{
                                                                    color: 'error.main',
                                                                    fontSize: 14,
                                                                    lineHeight: 1.45,
                                                                    wordBreak:
                                                                        'break-word' as const,
                                                                }}
                                                            >
                                                                There are not
                                                                enough meeting
                                                                rooms available.
                                                                Please change
                                                                the date.
                                                            </Typography>
                                                        )}
                                                    {availability?.privateOffices <
                                                        1 &&
                                                        spaceType ===
                                                            'privateOffice' && (
                                                            <Typography
                                                                variant='body3'
                                                                sx={{
                                                                    color: 'error.main',
                                                                    fontSize: 14,
                                                                    lineHeight: 1.45,
                                                                    wordBreak:
                                                                        'break-word' as const,
                                                                }}
                                                            >
                                                                There are not
                                                                enough private
                                                                offices
                                                                available.
                                                                Please change
                                                                the date.
                                                            </Typography>
                                                        )}
                                                </LabelRow>
                                            )}
                                            {(spaceType === 'desk' ||
                                                spaceType ===
                                                    'privateOffice') && (
                                                <LabelRow
                                                    label='Meeting Room Required?'
                                                    htmlFor='req-meeting-room__input'
                                                >
                                                    {canEditMeetingRoomRequired &&
                                                    !lockNonAttendeeFields ? (
                                                        <Stack spacing={1}>
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    maxWidth: 260,
                                                                    '& .MuiInputBase-input':
                                                                        {
                                                                            ...theme
                                                                                .typography
                                                                                .body3,
                                                                        },
                                                                }}
                                                            >
                                                                <FormCheckbox
                                                                    id='req-meeting-room__input'
                                                                    name='bookingInfo.reqMeetingRoom'
                                                                    control={
                                                                        control
                                                                    }
                                                                    onChange={(
                                                                        _,
                                                                        checked: boolean,
                                                                    ) =>
                                                                        handlReqMeetingRoomChange(
                                                                            checked,
                                                                        )
                                                                    }
                                                                />
                                                            </Box>

                                                            {mustCompleteMeetingRoomDetails && (
                                                                <Alert
                                                                    severity='info'
                                                                    sx={{
                                                                        width: '100%',
                                                                        alignItems:
                                                                            'flex-start',
                                                                        '& .MuiAlert-message':
                                                                            {
                                                                                ...theme
                                                                                    .typography
                                                                                    .body3,
                                                                            },
                                                                    }}
                                                                >
                                                                    You’ve
                                                                    requested a
                                                                    meeting
                                                                    room. Please
                                                                    complete all
                                                                    required
                                                                    meeting
                                                                    details
                                                                    below,
                                                                    including
                                                                    number of
                                                                    guests,
                                                                    attendees,
                                                                    and space
                                                                    selection,
                                                                    before
                                                                    saving.
                                                                </Alert>
                                                            )}

                                                            {availability?.meetingRooms <
                                                                1 &&
                                                                isMeetingRoom && (
                                                                    <Typography
                                                                        variant='body3'
                                                                        sx={{
                                                                            color: 'error.main',
                                                                            fontSize: 14,
                                                                            lineHeight: 1.45,
                                                                            wordBreak:
                                                                                'break-word' as const,
                                                                        }}
                                                                    >
                                                                        There
                                                                        are not
                                                                        enough
                                                                        meeting
                                                                        rooms
                                                                        available.
                                                                        Please
                                                                        change
                                                                        the
                                                                        date.
                                                                    </Typography>
                                                                )}
                                                        </Stack>
                                                    ) : (
                                                        <Typography
                                                            variant='body3'
                                                            sx={valueSx}
                                                        >
                                                            {isMeetingRoom
                                                                ? 'Yes'
                                                                : 'No'}
                                                        </Typography>
                                                    )}
                                                </LabelRow>
                                            )}
                                        </DetailCard>
                                    )}
                                    <DetailCard>
                                        {reservationDetails?.module?.type ===
                                            'activity' ||
                                        commercialAttendees ? (
                                            <LabelRow
                                                label='No. Guests'
                                                htmlFor='guests__input'
                                                required={
                                                    mustCompleteMeetingRoomDetails
                                                }
                                            >
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 260,
                                                        '& .MuiInputBase-input':
                                                            {
                                                                ...theme
                                                                    .typography
                                                                    .body3,
                                                            },
                                                        '& .MuiAutocomplete-input':
                                                            {
                                                                ...theme
                                                                    .typography
                                                                    .body3,
                                                            },
                                                    }}
                                                >
                                                    <FormCounter
                                                        id='guests__input'
                                                        name='bookingInfo.guests'
                                                        disabled={
                                                            lockNonAttendeeFields ||
                                                            (disableGuests &&
                                                                !commercialAttendees)
                                                        }
                                                        disableIncrement={
                                                            getIndividualAsset?.type ===
                                                                ExperienceType.Commercial &&
                                                            MAX_COMMERCIAL_GUEST_REACHED
                                                        }
                                                        align='flex-start'
                                                    />
                                                </Box>
                                            </LabelRow>
                                        ) : reservationDetails?.module?.type ===
                                          'vehicle' ? null : reservationDetails
                                              ?.module?.type ===
                                          'commercial' ? null : (
                                            <>
                                                <LabelRow
                                                    label='No. Adults'
                                                    htmlFor='adults__input'
                                                >
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            maxWidth: 260,
                                                            '& .MuiInputBase-input':
                                                                {
                                                                    ...theme
                                                                        .typography
                                                                        .body3,
                                                                },
                                                            '& .MuiAutocomplete-input':
                                                                {
                                                                    ...theme
                                                                        .typography
                                                                        .body3,
                                                                },
                                                        }}
                                                    >
                                                        <FormCounter
                                                            id='adults__input'
                                                            name='bookingInfo.adults'
                                                            disabled={
                                                                lockNonAttendeeFields
                                                            }
                                                            disableIncrement={
                                                                maxGuestCountReached
                                                            }
                                                            align='flex-start'
                                                        />
                                                    </Box>
                                                </LabelRow>

                                                <LabelRow
                                                    label='No. Children'
                                                    htmlFor='children__input'
                                                >
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            maxWidth: 260,
                                                            '& .MuiInputBase-input':
                                                                {
                                                                    ...theme
                                                                        .typography
                                                                        .body3,
                                                                },
                                                            '& .MuiAutocomplete-input':
                                                                {
                                                                    ...theme
                                                                        .typography
                                                                        .body3,
                                                                },
                                                        }}
                                                    >
                                                        <FormCounter
                                                            id='children__input'
                                                            name='bookingInfo.children'
                                                            disabled={
                                                                lockNonAttendeeFields
                                                            }
                                                            disableIncrement={
                                                                maxGuestCountReached
                                                            }
                                                            align='flex-start'
                                                        />
                                                    </Box>
                                                </LabelRow>

                                                {reservationDetails?.type ===
                                                    'vehicle' ||
                                                reservationDetails?.visitType ===
                                                    'personal' ||
                                                readableStartDate ===
                                                    readableEndDate ? null : (
                                                    <LabelRow
                                                        label='No. Overnight Guests'
                                                        htmlFor='overnight-guests__input'
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: '100%',
                                                                maxWidth: 260,
                                                                '& .MuiInputBase-input':
                                                                    {
                                                                        ...theme
                                                                            .typography
                                                                            .body3,
                                                                    },
                                                                '& .MuiAutocomplete-input':
                                                                    {
                                                                        ...theme
                                                                            .typography
                                                                            .body3,
                                                                    },
                                                            }}
                                                        >
                                                            <FormCounter
                                                                id='overnight-guests__input'
                                                                name='bookingInfo.overnightGuests'
                                                                disabled={
                                                                    lockNonAttendeeFields
                                                                }
                                                                disableIncrement={
                                                                    maxOvernightGuestReached
                                                                }
                                                                align='flex-start'
                                                            />
                                                        </Box>
                                                    </LabelRow>
                                                )}
                                            </>
                                        )}

                                        {getIndividualAsset?.visitType !==
                                            'personal' &&
                                            getIndividualAsset?.type !==
                                                ExperienceType.Vehicle &&
                                            (getIndividualAsset?.type !==
                                                ExperienceType.Commercial ||
                                                commercialAttendees) && (
                                                <FullWidthRow
                                                    label='Staff Attending'
                                                    htmlFor='staff-attending__input'
                                                    id='staff-attending__label'
                                                    alignLabelToContentStart
                                                    required={
                                                        mustCompleteMeetingRoomDetails
                                                    }
                                                >
                                                    {loggedInUser?.role.includes(
                                                        'owner',
                                                    ) &&
                                                    bookerType === 'guest' &&
                                                    (ownerCommercialAttendeesReadOnly ||
                                                        !lockNonAttendeeFields) ? (
                                                        renderAttendeeList(
                                                            reservationDetails?.staffAttending,
                                                            'No staff selected',
                                                        )
                                                    ) : bookerType ===
                                                      'guest' ? (
                                                        <StaffAttendees
                                                            experienceType={
                                                                getIndividualAsset?.type
                                                            }
                                                            usesFaceRec={
                                                                usesFaceRec
                                                            }
                                                            isEditing={true}
                                                        />
                                                    ) : (
                                                        <OwnerAttendeesSection
                                                            experienceDetails={
                                                                getIndividualAsset
                                                            }
                                                            usesFaceRec={
                                                                usesFaceRec
                                                            }
                                                            isEditing={true}
                                                        />
                                                    )}
                                                </FullWidthRow>
                                            )}

                                        {getIndividualAsset?.visitType !==
                                            'personal' &&
                                            getIndividualAsset?.type !==
                                                ExperienceType.Vehicle &&
                                            (getIndividualAsset?.type !==
                                                ExperienceType.Commercial ||
                                                commercialAttendees) && (
                                                <FullWidthRow
                                                    label='Guest Attendees'
                                                    htmlFor='guest-attendees__input'
                                                    id='guest-attendees__label'
                                                    alignLabelToContentStart
                                                    required={
                                                        mustCompleteMeetingRoomDetails
                                                    }
                                                >
                                                    {loggedInUser?.role.includes(
                                                        'owner',
                                                    ) &&
                                                    (ownerCommercialAttendeesReadOnly ||
                                                        !lockNonAttendeeFields) ? (
                                                        renderAttendeeList(
                                                            reservationDetails?.externalAttendees,
                                                            'No guest attendees selected',
                                                        )
                                                    ) : (
                                                        <ExternalAttendees
                                                            experienceType={
                                                                getIndividualAsset?.type
                                                            }
                                                            guestCount={
                                                                guestCount
                                                            }
                                                            usesFaceRec={
                                                                usesFaceRec
                                                            }
                                                            isEditing={true}
                                                        />
                                                    )}
                                                </FullWidthRow>
                                            )}
                                        {showSpaceSelection ? (
                                            <FullWidthRow
                                                label=''
                                                alignLabelToContentStart
                                            >
                                                <Divider
                                                    sx={{
                                                        mt: 3,
                                                        mb: 3,
                                                        color: theme.palette
                                                            .faded.main,
                                                    }}
                                                />
                                            </FullWidthRow>
                                        ) : null}
                                        {showSpaceSelection && (
                                            <FullWidthRow
                                                label='Room allocation'
                                                alignLabelToContentStart
                                            >
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        minWidth: 0,
                                                        position: 'relative',
                                                        zIndex: 5,
                                                        overflow: 'visible',
                                                        pointerEvents: 'auto',
                                                        ...nonInteractiveLockSx,
                                                    }}
                                                >
                                                    <ReservationSpaces
                                                        name='spaces'
                                                        spaces={
                                                            spacesWithAvailability
                                                        }
                                                        hasByZoneCleaningFee={
                                                            hasByZoneCleaningFee
                                                        }
                                                        bookingStartDate={moment(
                                                            startDate,
                                                        ).format('YYYY-MM-DD')}
                                                        bookingEndDate={moment(
                                                            endDate,
                                                        ).format('YYYY-MM-DD')}
                                                        bookingCheckinTime={
                                                            checkinTime
                                                        }
                                                        bookingCheckoutTime={
                                                            checkoutTime
                                                        }
                                                        experienceDetails={
                                                            getIndividualAsset
                                                        }
                                                        spaceType={spaceType}
                                                        isMeetingRoom={
                                                            isMeetingRoom
                                                        }
                                                        handleError={() =>
                                                            setSpaceError(true)
                                                        }
                                                        clearError={() =>
                                                            setSpaceError(false)
                                                        }
                                                    />
                                                </Box>
                                            </FullWidthRow>
                                        )}
                                    </DetailCard>
                                </>
                            )}

                            {contractorsForPolicies.length > 0 &&
                                !showOptionalServicesSection && (
                                    <>
                                        <Divider
                                            sx={{
                                                borderColor:
                                                    theme.palette.custom
                                                        .lightBorderColor,
                                            }}
                                        />
                                        <DetailsSubsection
                                            title='Contractor policies'
                                            description="Contractors operate independently from experience owners. Read and agree to each supplier's cancellation policy before saving."
                                        >
                                            <DetailSurround>
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        ...nonInteractiveLockSx,
                                                    }}
                                                >
                                                    <ContractorPolicies
                                                        contractors={
                                                            contractorsForPolicies
                                                        }
                                                        isReservationEditForm={
                                                            true
                                                        }
                                                        hideIntroCaption
                                                        jobs={
                                                            contractorJobsArray
                                                        }
                                                    />
                                                </Box>
                                            </DetailSurround>
                                        </DetailsSubsection>
                                    </>
                                )}
                        </>
                    </EditAccordionSection>
                )}

                {activeNav === 'services' && showOptionalServicesSection && (
                    <EditAccordionSection
                        title='Optional Services'
                        subtitle='Enhance your stay with additional services. Select any services you’d like to request as part of this reservation.'
                        expanded={true}
                    >
                        <>
                            {shouldShowJobForm && (
                                <>
                                    <DetailCard>
                                        <LabelRow
                                            label='Add additional services to this reservation'
                                            fontWeight='none'
                                            htmlFor='requires-additional-services__input'
                                        >
                                            <Stack
                                                spacing={1}
                                                alignItems='flex-start'
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: '100%',
                                                }}
                                            >
                                                <CheckboxRowContent>
                                                    <FormCheckbox
                                                        id='requires-additional-services__input'
                                                        name='requiresServices'
                                                        control={control}
                                                        sx={{ p: 0 }}
                                                        disabled={
                                                            optionalServicesReadOnly
                                                        }
                                                        onChange={(
                                                            _,
                                                            checked: boolean,
                                                        ) =>
                                                            handleRequiresServiceChange(
                                                                undefined,
                                                                checked,
                                                            )
                                                        }
                                                    />
                                                </CheckboxRowContent>
                                                {reservationDetails?.requiresServices &&
                                                    !requiresServices &&
                                                    hasJobs && (
                                                        <Alert
                                                            severity='warning'
                                                            sx={{
                                                                width: '100%',
                                                                borderRadius: 2,
                                                                alignSelf:
                                                                    'stretch',
                                                                '& .MuiAlert-message':
                                                                    {
                                                                        ...theme
                                                                            .typography
                                                                            .body3,
                                                                    },
                                                            }}
                                                        >
                                                            Saving this change
                                                            will cancel all
                                                            optional service
                                                            jobs created for
                                                            this reservation.
                                                        </Alert>
                                                    )}
                                            </Stack>
                                        </LabelRow>

                                        {requiresServices && (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    gridColumn: {
                                                        xs: '1',
                                                        md: '1 / -1',
                                                    },
                                                }}
                                            >
                                                <ReservationJobs
                                                    experienceDetails={
                                                        getIndividualAsset
                                                    }
                                                    dateOptions={
                                                        reservationDateOptions
                                                    }
                                                    isEditing={true}
                                                    embedInEditDetails
                                                />
                                            </Box>
                                        )}
                                    </DetailCard>
                                </>
                            )}

                            {!shouldShowJobForm && (
                                <>
                                    {getIndividualAsset?.details?.catering && (
                                        <DetailsSubsection
                                            title='Catering'
                                            description={
                                                'Arrange catering and describe what you need. The host will coordinate.'
                                            }
                                        >
                                            <DetailCard>
                                                <LabelRow
                                                    label='Add catering to this reservation'
                                                    fontWeight='none'
                                                    htmlFor='requires-catering__input'
                                                >
                                                    <Stack
                                                        spacing={1}
                                                        alignItems='flex-start'
                                                        sx={{
                                                            width: '100%',
                                                            maxWidth: '100%',
                                                        }}
                                                    >
                                                        <CheckboxRowContent>
                                                            <FormCheckbox
                                                                id='requires-catering__input'
                                                                name='bookingInfo.requiresCatering'
                                                                control={
                                                                    control
                                                                }
                                                                sx={{ p: 0 }}
                                                                disabled={
                                                                    optionalServicesReadOnly
                                                                }
                                                                onChange={(
                                                                    _,
                                                                    checked: boolean,
                                                                ) =>
                                                                    handleRequiresServiceChange(
                                                                        ServiceType.Catering,
                                                                        checked,
                                                                    )
                                                                }
                                                            />
                                                        </CheckboxRowContent>
                                                    </Stack>
                                                </LabelRow>

                                                {requiresCatering && (
                                                    <LabelRow
                                                        label='Details'
                                                        htmlFor='catering-details__input'
                                                    >
                                                        <Stack
                                                            spacing={0.5}
                                                            sx={{
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={
                                                                    optionalServiceNotesSx
                                                                }
                                                            >
                                                                <FormTextField
                                                                    id='catering-details__input'
                                                                    name='bookingInfo.cateringNotes'
                                                                    control={
                                                                        control
                                                                    }
                                                                    disabled={
                                                                        optionalServicesReadOnly
                                                                    }
                                                                    inputProps={{
                                                                        maxLength: 500,
                                                                    }}
                                                                    multiline
                                                                    rows={4}
                                                                    fullWidth
                                                                />
                                                            </Box>
                                                            {cateringDetails?.length >
                                                                0 && (
                                                                <Typography
                                                                    sx={
                                                                        charCountSx
                                                                    }
                                                                >
                                                                    Characters
                                                                    left:{' '}
                                                                    {500 -
                                                                        cateringDetails?.length}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </LabelRow>
                                                )}
                                            </DetailCard>
                                        </DetailsSubsection>
                                    )}

                                    {getIndividualAsset?.details?.catering &&
                                        (getIndividualAsset?.details?.alcohol ||
                                            getIndividualAsset?.details
                                                ?.massages) && (
                                            <Divider
                                                sx={{
                                                    mb: isMobile ? 1 : 2,
                                                    borderColor:
                                                        theme.palette.custom
                                                            .lightBorderColor,
                                                }}
                                            />
                                        )}

                                    {getIndividualAsset?.details?.alcohol && (
                                        <DetailsSubsection
                                            title='Beverages'
                                            description='Request beverages, bar service, or drinks packages for your booking.'
                                        >
                                            <DetailCard>
                                                <LabelRow
                                                    label='Add beverages to this reservation'
                                                    fontWeight='none'
                                                    htmlFor='requires-beverages__input'
                                                >
                                                    <Stack
                                                        spacing={1}
                                                        alignItems='flex-start'
                                                        sx={{
                                                            width: '100%',
                                                            maxWidth: '100%',
                                                        }}
                                                    >
                                                        <CheckboxRowContent>
                                                            <FormCheckbox
                                                                id='requires-beverages__input'
                                                                name='bookingInfo.requiresBeverages'
                                                                control={
                                                                    control
                                                                }
                                                                disabled={
                                                                    optionalServicesReadOnly
                                                                }
                                                                onChange={(
                                                                    _,
                                                                    checked: boolean,
                                                                ) =>
                                                                    handleRequiresServiceChange(
                                                                        ServiceType.Alcohol,
                                                                        checked,
                                                                    )
                                                                }
                                                                sx={{ p: 0 }}
                                                            />
                                                        </CheckboxRowContent>
                                                    </Stack>
                                                </LabelRow>

                                                {requiresBeverages && (
                                                    <LabelRow
                                                        label='Details'
                                                        htmlFor='beverage-details__input'
                                                    >
                                                        <Stack
                                                            spacing={0.5}
                                                            sx={{
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={
                                                                    optionalServiceNotesSx
                                                                }
                                                            >
                                                                <FormTextField
                                                                    id='beverage-details__input'
                                                                    name='bookingInfo.beverageNotes'
                                                                    control={
                                                                        control
                                                                    }
                                                                    disabled={
                                                                        optionalServicesReadOnly
                                                                    }
                                                                    inputProps={{
                                                                        maxLength: 500,
                                                                    }}
                                                                    multiline
                                                                    rows={4}
                                                                    fullWidth
                                                                />
                                                            </Box>
                                                            {beverageDetails?.length >
                                                                0 && (
                                                                <Typography
                                                                    sx={
                                                                        charCountSx
                                                                    }
                                                                >
                                                                    Characters
                                                                    left:{' '}
                                                                    {500 -
                                                                        beverageDetails?.length}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </LabelRow>
                                                )}
                                            </DetailCard>
                                        </DetailsSubsection>
                                    )}

                                    {getIndividualAsset?.details?.alcohol &&
                                        getIndividualAsset?.details
                                            ?.massages && (
                                            <Divider
                                                sx={{
                                                    mb: isMobile ? 1 : 2,
                                                    borderColor:
                                                        theme.palette.custom
                                                            .lightBorderColor,
                                                }}
                                            />
                                        )}

                                    {getIndividualAsset?.details?.massages && (
                                        <DetailsSubsection
                                            title='Wellness & lifestyle'
                                            description={
                                                'Book wellness & lifestyle services and describe what you need in the notes field.'
                                            }
                                        >
                                            <DetailCard>
                                                <LabelRow
                                                    label='Add wellness & lifestyle services to this reservation'
                                                    fontWeight='none'
                                                    htmlFor='requires-massages__input'
                                                >
                                                    <Stack
                                                        spacing={1}
                                                        alignItems='flex-start'
                                                        sx={{
                                                            width: '100%',
                                                            maxWidth: '100%',
                                                        }}
                                                    >
                                                        <CheckboxRowContent>
                                                            <FormCheckbox
                                                                id='requires-massages__input'
                                                                name='bookingInfo.requiresMassages'
                                                                control={
                                                                    control
                                                                }
                                                                disabled={
                                                                    optionalServicesReadOnly
                                                                }
                                                                onChange={(
                                                                    _,
                                                                    checked: boolean,
                                                                ) =>
                                                                    handleRequiresServiceChange(
                                                                        ServiceType.Wellness,
                                                                        checked,
                                                                    )
                                                                }
                                                                sx={{ p: 0 }}
                                                            />
                                                        </CheckboxRowContent>
                                                    </Stack>
                                                </LabelRow>

                                                {requiresMassages && (
                                                    <LabelRow
                                                        label='Notes'
                                                        htmlFor='massage-notes__input'
                                                    >
                                                        <Stack
                                                            spacing={0.5}
                                                            sx={{
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={
                                                                    optionalServiceNotesSx
                                                                }
                                                            >
                                                                <FormTextField
                                                                    id='massage-notes__input'
                                                                    name='bookingInfo.massageNotes'
                                                                    control={
                                                                        control
                                                                    }
                                                                    disabled={
                                                                        optionalServicesReadOnly
                                                                    }
                                                                    inputProps={{
                                                                        maxLength: 500,
                                                                    }}
                                                                    multiline
                                                                    rows={4}
                                                                    fullWidth
                                                                />
                                                            </Box>

                                                            {massageDetails?.length >
                                                                0 && (
                                                                <Typography
                                                                    sx={
                                                                        charCountSx
                                                                    }
                                                                >
                                                                    Characters
                                                                    left:{' '}
                                                                    {500 -
                                                                        massageDetails?.length}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </LabelRow>
                                                )}
                                            </DetailCard>
                                        </DetailsSubsection>
                                    )}
                                </>
                            )}

                            {contractorsForPolicies.length > 0 && (
                                <>
                                    <Divider
                                        sx={{
                                            mb: isMobile ? 1 : 2,
                                            borderColor:
                                                theme.palette.custom
                                                    .lightBorderColor,
                                        }}
                                    />
                                    <DetailsSubsection
                                        title='Contractor policies'
                                        description="Contractors operate independently from experience owners. Read and agree to each supplier's cancellation policy before saving."
                                    >
                                        <DetailSurround>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    ...nonInteractiveLockSx,
                                                }}
                                            >
                                                <ContractorPolicies
                                                    contractors={
                                                        contractorsForPolicies
                                                    }
                                                    jobs={contractorJobsArray}
                                                    isReservationEditForm={true}
                                                    hideIntroCaption
                                                />
                                            </Box>
                                        </DetailSurround>
                                    </DetailsSubsection>
                                </>
                            )}
                        </>
                    </EditAccordionSection>
                )}

                {activeNav === 'notes' && (
                    <EditAccordionSection
                        title='Additional Notes'
                        // subtitle='Optional context for the experience host.'
                        expanded={true}
                    >
                        <DetailCard>
                            <Box
                                sx={{
                                    gridColumn: { xs: '1', md: '1 / -1' },
                                    width: '100%',
                                    minWidth: 0,
                                }}
                            >
                                <Stack spacing={0.5} sx={{ width: '100%' }}>
                                    <Box sx={optionalServiceNotesSx}>
                                        <FormTextField
                                            name='bookingInfo.notes'
                                            control={control}
                                            fullWidth
                                            multiline
                                            rows={4}
                                            disabled={lockNonAttendeeFields}
                                        />
                                    </Box>
                                    {additionalNotes?.length > 0 && (
                                        <Typography sx={charCountSx}>
                                            Characters left:{' '}
                                            {300 - additionalNotes?.length}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </DetailCard>
                    </EditAccordionSection>
                )}

                {activeNav === 'issues' && hasIssuesSection && (
                    <EditAccordionSection title='Review issues' expanded={true}>
                        <DetailSurround>
                            <Alert
                                severity='error'
                                sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    alignItems: 'flex-start',
                                    '& .MuiAlert-message': {
                                        width: '100%',
                                        ...theme.typography.body3,
                                        lineHeight: 1.35,
                                    },
                                }}
                            >
                                <Stack spacing={1}>
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            fontWeight: 800,
                                            lineHeight: 1.35,
                                            color: 'error.dark',
                                        }}
                                    >
                                        Fix these before saving (check the tabs
                                        above for each item):
                                    </Typography>

                                    <Box
                                        component='ul'
                                        sx={{
                                            m: 0,
                                            pl: 2,
                                            '& li': { mb: 0.35 },
                                        }}
                                    >
                                        {reviewIssues.map((issue, i) => (
                                            <Typography
                                                key={`${i}-${issue}`}
                                                component='li'
                                                variant='body3'
                                                sx={{
                                                    ...valueSx,
                                                    color: 'inherit',
                                                }}
                                            >
                                                {issue}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Stack>
                            </Alert>
                        </DetailSurround>
                    </EditAccordionSection>
                )}
            </Box>
        </ReservationEditDetailsLayoutContext.Provider>
    );
}
